import { useState, useRef, useEffect, useCallback } from 'react';
import request from '../../../request'; 

export type AudioPlaybackState = 'idle' | 'loading' | 'playing' | 'paused';

export const useAudioPlayer = () => {
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [playbackState, setPlaybackState] = useState<AudioPlaybackState>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCacheRef = useRef<Map<string, string>>(new Map());

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setActiveMessageId(null);
    setPlaybackState('idle');
  }, []);

  const playAudio = useCallback(async (messageId: string, text: string) => {
    if (activeMessageId === messageId && playbackState === 'playing') {
      audioRef.current?.pause();
      setPlaybackState('paused');
      return;
    }

    if (activeMessageId === messageId && playbackState === 'paused') {
      audioRef.current?.play();
      setPlaybackState('playing');
      return;
    }

    stopAudio();
    setActiveMessageId(messageId);
    setPlaybackState('loading');


    try {
      let audioUrl = audioCacheRef.current.get(messageId);

      if (!audioUrl) {
        const response = await request.post(
          `${process.env.REACT_APP_API_URL}/intelligence/audio/speak`,
          { text },
          { responseType: 'blob' }
        );

        let blob: Blob;

        if (response?.data?.data && Array.isArray(response.data.data)) {
          const byteArray = new Uint8Array(response.data.data);
          blob = new Blob([byteArray], { type: 'audio/mpeg' });
        }
        else if (response?.data instanceof ArrayBuffer) {
          blob = new Blob([response.data], { type: 'audio/mpeg' });
        }
        else if (response?.data instanceof Blob) {
          blob = response.data;
        } else {
          throw new Error('Unexpected audio response format');
        }

        audioUrl = URL.createObjectURL(blob);
        audioCacheRef.current.set(messageId, audioUrl);
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => setPlaybackState('playing');
      audio.onpause = () => {
        if (audio.currentTime > 0 && !audio.ended) {
          setPlaybackState('paused');
        }
      };
      audio.onended = () => {
        setActiveMessageId(null);
        setPlaybackState('idle');
      };
      audio.onerror = (e) => {
        console.error('Audio playback element error:', e);
        stopAudio();
      };

      await audio.play();
    } catch (err) {
      console.error('Audio playback error:', err);
      stopAudio();
    }
  }, [activeMessageId, playbackState, stopAudio]);

  useEffect(() => {
    return () => {
      stopAudio();
      audioCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      audioCacheRef.current.clear();
    };
  }, [stopAudio]);

  return {
    activeMessageId,
    playbackState,
    playAudio,
    stopAudio,
  };
};